import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react'

const NEAR_BOTTOM_PX = 80

function isNearBottom(element: HTMLElement, threshold: number): boolean {
  const distanceFromBottom =
    element.scrollHeight - element.scrollTop - element.clientHeight
  return distanceFromBottom <= threshold
}

export interface UseStickToBottomParams {
  /** Element whose size changes should re-pin the scroll container. */
  contentRef: React.RefObject<HTMLElement | null>
  /** Values that, when changed, re-scroll to the bottom while pinned. */
  scrollDeps: React.DependencyList
  /** When true, resets to the top and re-arms stick-to-bottom. */
  resetWhenEmpty: boolean
  /** Distance from the bottom (px) still treated as "at the bottom". */
  nearBottomPx?: number
}

export interface UseStickToBottomResult {
  scrollRef: React.RefObject<HTMLDivElement>
  onScroll: () => void
}

/**
 * Keeps a scroll container pinned to the bottom as content grows, but only
 * while the user is already near the bottom. Scrolling up releases the pin so
 * expanding content (e.g. thoughts) does not yank the viewport.
 */
export function useStickToBottom({
  contentRef,
  scrollDeps,
  resetWhenEmpty,
  nearBottomPx = NEAR_BOTTOM_PX,
}: UseStickToBottomParams): UseStickToBottomResult {
  const scrollRef = useRef<HTMLDivElement>(null)
  const stickToBottomRef = useRef(true)

  const scrollToBottom = useCallback(() => {
    const container = scrollRef.current
    if (!container || !stickToBottomRef.current) return
    window.requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight
    })
  }, [])

  const onScroll = useCallback(() => {
    const container = scrollRef.current
    if (!container) return
    stickToBottomRef.current = isNearBottom(container, nearBottomPx)
  }, [nearBottomPx])

  useLayoutEffect(() => {
    scrollToBottom()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scrollDeps is the caller-owned content key
  }, [scrollToBottom, ...scrollDeps])

  useEffect(() => {
    const container = scrollRef.current
    if (!container || !resetWhenEmpty) return
    stickToBottomRef.current = true
    container.scrollTop = 0
  }, [resetWhenEmpty])

  useEffect(() => {
    const content = contentRef.current
    if (!content) return
    const observer = new ResizeObserver(() => {
      scrollToBottom()
    })
    observer.observe(content)
    return () => observer.disconnect()
  }, [contentRef, scrollToBottom])

  return { scrollRef, onScroll }
}
